import { createContext, useContext, useState, useEffect, useCallback, PropsWithChildren } from 'react';
import { useApi, fetchApiRef, configApiRef, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { HARNESS_CONTEXT_REFRESH_INTERVAL_MS } from '../../polling-config';

export interface HarnessOrg { identifier: string; name: string; }
export interface HarnessTrackedProject { orgId: string; projectId: string; projectName?: string; }

interface HarnessContextValue {
  orgs: HarnessOrg[];
  userOrgs: HarnessOrg[] | null;
  trackedProjects: HarnessTrackedProject[];
  orgsLoading: boolean;
  error: string;
  refreshTracked: () => Promise<void>;
  toggleTrack: (orgId: string, projectId: string, projectName?: string) => Promise<void>;
  accountId: string;
}

const HarnessContext = createContext<HarnessContextValue | null>(null);

export const HarnessProvider = ({ children }: PropsWithChildren<{}>) => {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const accountId = config.getOptionalString('harness.accountId') ?? '';

  const [orgs, setOrgs] = useState<HarnessOrg[]>([]);
  const [userOrgs, setUserOrgs] = useState<HarnessOrg[] | null>(null);
  const [trackedProjects, setTrackedProjects] = useState<HarnessTrackedProject[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [error, setError] = useState('');

  const proxyBase = useCallback(async () => {
    const base = await discoveryApi.getBaseUrl('proxy');
    return `${base}/harness`;
  }, [discoveryApi]);

  const okdBase = useCallback(async () => discoveryApi.getBaseUrl('portal'), [discoveryApi]);

  const refreshTracked = useCallback(async () => {
    try {
      const base = await okdBase();
      const resp = await fetchApi.fetch(`${base}/harness-tracked`);
      if (resp.ok) setTrackedProjects(await resp.json());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load tracked projects');
    }
  }, [fetchApi, okdBase]);

  const toggleTrack = useCallback(async (orgId: string, projectId: string, projectName?: string) => {
    try {
      const base = await okdBase();
      const alreadyTracked = trackedProjects.some(t => t.orgId === orgId && t.projectId === projectId);
      if (alreadyTracked) {
        const resp = await fetchApi.fetch(`${base}/harness-tracked/${encodeURIComponent(orgId)}/${encodeURIComponent(projectId)}`, { method: 'DELETE' });
        if (resp.ok) { setTrackedProjects(await resp.json()); return; }
      } else {
        const resp = await fetchApi.fetch(`${base}/harness-tracked`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orgId, projectId, projectName: projectName ?? projectId }),
        });
        if (resp.ok) { setTrackedProjects(await resp.json()); return; }
      }
      await refreshTracked();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to toggle tracking');
    }
  }, [fetchApi, okdBase, trackedProjects, refreshTracked]);

  useEffect(() => {
    if (!accountId) return;
    setOrgsLoading(true);
    (async () => {
      try {
        const base = await proxyBase();
        const resp = await fetchApi.fetch(`${base}/ng/api/organizations?accountIdentifier=${accountId}&pageSize=200`);
        if (!resp.ok) return;
        const data = await resp.json();
        const items: HarnessOrg[] = (data.data?.content ?? []).map((o: any) => ({
          identifier: o.organization.identifier,
          name: o.organization.name,
        }));
        setOrgs(items);

        // Best-effort user org filter
        try {
          const profile = await identityApi.getProfileInfo();
          const email = profile.email;
          if (email) {
            const ur = await fetchApi.fetch(`${base}/ng/api/user/aggregate?accountIdentifier=${accountId}&searchTerm=${encodeURIComponent(email)}`);
            if (ur.ok) {
              const ud = await ur.json();
              const userId = ud.data?.content?.[0]?.user?.uuid as string | undefined;
              if (userId) {
                const filtered = await fetchApi.fetch(`${base}/ng/api/organizations?accountIdentifier=${accountId}&userId=${userId}&pageSize=200`);
                if (filtered.ok) {
                  const fd = await filtered.json();
                  const filteredItems: HarnessOrg[] = (fd.data?.content ?? []).map((o: any) => ({
                    identifier: o.organization.identifier,
                    name: o.organization.name,
                  }));
                  if (filteredItems.length > 0 && filteredItems.length < items.length) {
                    setUserOrgs(filteredItems);
                  }
                }
              }
            }
          }
        } catch { /* user filter is best-effort */ }
      } catch { /* ignore */ } finally {
        setOrgsLoading(false);
      }
    })();

    refreshTracked();
    if (HARNESS_CONTEXT_REFRESH_INTERVAL_MS > 0) {
      const id = setInterval(() => refreshTracked(), HARNESS_CONTEXT_REFRESH_INTERVAL_MS);
      return () => clearInterval(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HarnessContext.Provider value={{ orgs, userOrgs, trackedProjects, orgsLoading, error, refreshTracked, toggleTrack, accountId }}>
      {children}
    </HarnessContext.Provider>
  );
};

export const useHarnessData = (): HarnessContextValue => {
  const ctx = useContext(HarnessContext);
  if (!ctx) throw new Error('useHarnessData must be used within HarnessProvider');
  return ctx;
};
